// \src\utils\rss.ts

import type { NewsItem, RssSource } from "@/context/AdminContext";

const CACHE_KEY = "mz_rss_cache_v6";
const OG_CACHE_KEY = "mz_og_image_cache_v3";

const CACHE_TTL_MINUTES = 30;
const MAX_NEWS = 30;
const MAX_NEWS_PER_SOURCE = 3;

const RSS2JSON_TIMEOUT_MS = 5500;
const DIRECT_FEED_TIMEOUT_MS = 2200;
const PROXY_FEED_TIMEOUT_MS = 4500;
const OG_IMAGE_TIMEOUT_MS = 4500;

const MAX_OG_LOOKUPS_PER_LOAD = 18;
const OG_LOOKUP_CONCURRENCY = 3;

const OG_CACHE_SUCCESS_TTL = 24 * 60 * 60 * 1000;
const OG_CACHE_EMPTY_TTL = 6 * 60 * 60 * 1000;

const PROXIES = [
  "https://api.allorigins.win/raw?url=",
  "https://corsproxy.io/?url=",
];

type ProgressCallback = (news: NewsItem[]) => void;
type ImageUpdateCallback = (
  item: NewsItem,
  news: NewsItem[],
) => void;

interface OgCacheEntry {
  image: string;
  ts: number;
}

interface CacheEntry {
  news: NewsItem[];
  timestamp: number;
  sourcesHash: string;
}

function cleanText(str: string): string {
  return (str || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanImageUrl(url: string): string {
  return (url || "")
    .trim()
    .replace(/&amp;/gi, "&")
    .replace(/^["']|["']$/g, "");
}

function extractFirstSrcsetUrl(srcset: string): string {
  if (!srcset) {
    return "";
  }

  const first = srcset
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)[0];

  if (!first) {
    return "";
  }

  return first.split(/\s+/)[0] || "";
}

function extractImageFromHtml(html: string): string {
  if (!html) {
    return "";
  }

  const candidates = [
    /<img[^>]+src=["']([^"']+)["']/i,
    /<img[^>]+data-src=["']([^"']+)["']/i,
    /<img[^>]+data-lazy-src=["']([^"']+)["']/i,
    /<img[^>]+data-original=["']([^"']+)["']/i,
    /property=["']og:image(?::secure_url)?["'][^>]*content=["']([^"']+)["']/i,
    /content=["']([^"']+)["'][^>]*property=["']og:image(?::secure_url)?["']/i,
    /name=["']twitter:image(?::src)?["'][^>]*content=["']([^"']+)["']/i,
    /content=["']([^"']+)["'][^>]*name=["']twitter:image(?::src)?["']/i,
  ];

  for (const pattern of candidates) {
    const match = html.match(pattern);

    if (match?.[1]) {
      return cleanImageUrl(match[1]);
    }
  }

  const srcsetMatch = html.match(
    /<img[^>]+(?:srcset|data-srcset)=["']([^"']+)["']/i,
  );

  if (srcsetMatch?.[1]) {
    return cleanImageUrl(
      extractFirstSrcsetUrl(srcsetMatch[1]),
    );
  }

  return "";
}

function resolveUrl(url: string, base: string): string {
  const cleaned = cleanImageUrl(url);

  if (!cleaned) {
    return "";
  }

  if (
    cleaned.startsWith("data:") ||
    cleaned.startsWith("blob:") ||
    cleaned.startsWith("javascript:")
  ) {
    return "";
  }

  if (cleaned.startsWith("//")) {
    return `https:${cleaned}`;
  }

  if (/^https?:\/\//i.test(cleaned)) {
    return cleaned;
  }

  try {
    return new URL(cleaned, base).href;
  } catch {
    return "";
  }
}

function looksLikeImageUrl(url: string): boolean {
  if (!url) {
    return false;
  }

  return /\.(?:avif|gif|jpe?g|png|webp)(?:$|[?#])/i.test(
    url,
  );
}

function getEnclosureImage(
  element: Element | undefined,
): string {
  if (!element) {
    return "";
  }

  const url =
    element.getAttribute("url") ||
    element.getAttribute("href") ||
    "";

  const type =
    element.getAttribute("type") ||
    "";

  if (
    type.toLowerCase().startsWith("image/") ||
    looksLikeImageUrl(url)
  ) {
    return url;
  }

  return "";
}

async function fetchWithTimeout(
  url: string,
  ms: number,
): Promise<string> {
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    ms,
  );

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(
        `HTTP ${response.status}`,
      );
    }

    return await response.text();
  } finally {
    window.clearTimeout(timer);
  }
}

async function fetchJsonWithTimeout(
  url: string,
  ms: number,
): Promise<unknown> {
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    ms,
  );

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(
        `HTTP ${response.status}`,
      );
    }

    return await response.json();
  } finally {
    window.clearTimeout(timer);
  }
}

function firstSuccessful<T>(
  tasks: Array<() => Promise<T>>,
  isValid: (value: T) => boolean = () => true,
): Promise<T> {
  return new Promise<T>(
    (resolve, reject) => {
      if (tasks.length === 0) {
        reject(
          new Error(
            "Nenhuma tentativa disponível.",
          ),
        );
        return;
      }

      let pending = tasks.length;
      let settled = false;
      let lastError: unknown;

      tasks.forEach((task) => {
        void task()
          .then((value) => {
            if (
              settled ||
              !isValid(value)
            ) {
              if (!isValid(value)) {
                lastError = new Error(
                  "Resposta vazia.",
                );
                pending -= 1;

                if (
                  pending === 0 &&
                  !settled
                ) {
                  reject(lastError);
                }
              }

              return;
            }

            settled = true;
            resolve(value);
          })
          .catch((error) => {
            lastError = error;
            pending -= 1;

            if (
              pending === 0 &&
              !settled
            ) {
              reject(
                lastError ||
                  new Error(
                    "Todas as tentativas falharam.",
                  ),
              );
            }
          });
      });
    },
  );
}

function stableNewsId(item: NewsItem): number {
  const value =
    item.link ||
    `${item.source}|${item.title}|${item.date}`;

  let hash = 0;

  for (
    let index = 0;
    index < value.length;
    index += 1
  ) {
    hash =
      (hash * 31 +
        value.charCodeAt(index)) |
      0;
  }

  const positive =
    Math.abs(hash) || 1;

  return positive;
}

function finalizeNews(
  items: NewsItem[],
): NewsItem[] {
  const seen = new Set<string>();

  const unique = items.filter((item) => {
    const key =
      item.link ||
      `${item.source}|${item.title}|${item.date}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);

    return true;
  });

  unique.sort(
    (a, b) =>
      new Date(b.date).getTime() -
      new Date(a.date).getTime(),
  );

  const sourceCounts = new Map<string, number>();
  const selected: NewsItem[] = [];

  for (const item of unique) {
    const sourceKey =
      item.source.trim().toLowerCase() ||
      "sem-fonte";

    const sourceCount =
      sourceCounts.get(sourceKey) || 0;

    if (sourceCount >= MAX_NEWS_PER_SOURCE) {
      continue;
    }

    selected.push({
      ...item,
      id: stableNewsId(item),
    });

    sourceCounts.set(
      sourceKey,
      sourceCount + 1,
    );

    if (selected.length >= MAX_NEWS) {
      break;
    }
  }

  return selected;
}

export function estimateReadTime(
  text: string,
): string {
  const words = (text || "")
    .split(/\s+/)
    .filter(Boolean).length;

  const minutes = Math.max(
    1,
    Math.round(words / 200),
  );

  return `${minutes} min`;
}

async function fetchViaRss2json(
  feedUrl: string,
  configuredSourceName: string,
): Promise<NewsItem[]> {
  const apiUrl =
    "https://api.rss2json.com/v1/api.json?rss_url=" +
    encodeURIComponent(feedUrl);

  const data = (await fetchJsonWithTimeout(
    apiUrl,
    RSS2JSON_TIMEOUT_MS,
  )) as {
    status?: string;
    feed?: {
      title?: string;
    };
    items?: Array<{
      title?: string;
      description?: string;
      content?: string;
      link?: string;
      pubDate?: string;
      thumbnail?: string;
      enclosure?: {
        link?: string;
        url?: string;
        type?: string;
      };
    }>;
  };

  if (
    data.status !== "ok" ||
    !Array.isArray(data.items)
  ) {
    throw new Error(
      "rss2json falhou",
    );
  }

  const sourceName =
    configuredSourceName ||
    data.feed?.title ||
    "Notícias";

  return data.items.map(
    (item, index) => {
      const rawContent =
        item.content ||
        item.description ||
        "";

      const excerpt =
        cleanText(rawContent);

      const parsedDate =
        new Date(item.pubDate || "");

      const date = !Number.isNaN(
        parsedDate.getTime(),
      )
        ? parsedDate.toISOString()
        : new Date().toISOString();

      const thumbnail =
        item.thumbnail || "";

      const enclosure =
        item.enclosure || {};

      const enclosureUrl =
        enclosure.link ||
        enclosure.url ||
        "";

      const enclosureImage =
        (enclosure.type || "")
          .toLowerCase()
          .startsWith("image/") ||
        looksLikeImageUrl(
          enclosureUrl,
        )
          ? enclosureUrl
          : "";

      const fromContent =
        extractImageFromHtml(
          rawContent,
        );

      const image = resolveUrl(
        thumbnail ||
          enclosureImage ||
          fromContent,
        feedUrl,
      );

      return {
        id: index + 1,
        title: cleanText(
          item.title || "Sem título",
        ),
        excerpt:
          excerpt.slice(0, 180),
        category: sourceName,
        date,
        image,
        readTime:
          estimateReadTime(excerpt),
        source: sourceName,
        link: resolveUrl(
          item.link || "",
          feedUrl,
        ),
      };
    },
  );
}

function parseRssItems(
  doc: Document,
  baseUrl: string,
): Array<{
  title: string;
  content: string;
  link: string;
  date: string;
  image: string;
}> {
  return Array.from(
    doc.querySelectorAll("item"),
  ).map((element) => {
    const title = cleanText(
      element.querySelector("title")
        ?.textContent ||
        "Sem título",
    );

    const contentEncoded =
      element.getElementsByTagName(
        "content:encoded",
      )[0]?.textContent || "";

    const description =
      element.querySelector(
        "description",
      )?.textContent || "";

    const encoded =
      element.querySelector(
        "encoded",
      )?.textContent || "";

    const content =
      contentEncoded ||
      encoded ||
      description;

    const link =
      element.querySelector("link")
        ?.textContent?.trim() || "";

    const pubRaw =
      element.querySelector("pubDate")
        ?.textContent ||
      element.querySelector(
        "dc\\:date",
      )?.textContent ||
      "";

    const mediaContent =
      element.getElementsByTagName(
        "media:content",
      )[0] as
        | Element
        | undefined;

    const mediaThumbnail =
      element.getElementsByTagName(
        "media:thumbnail",
      )[0] as
        | Element
        | undefined;

    const enclosure =
      element.getElementsByTagName(
        "enclosure",
      )[0] as
        | Element
        | undefined;

    const itunesImage =
      element.getElementsByTagName(
        "itunes:image",
      )[0] as
        | Element
        | undefined;

    const mediaImage =
      mediaContent?.getAttribute(
        "url",
      ) || "";

    const thumbnailImage =
      mediaThumbnail?.getAttribute(
        "url",
      ) || "";

    const enclosureImage =
      getEnclosureImage(
        enclosure,
      );

    const itunesImageUrl =
      itunesImage?.getAttribute(
        "href",
      ) || "";

    const contentImage =
      extractImageFromHtml(
        contentEncoded ||
          description,
      );

    const image = resolveUrl(
      mediaImage ||
        thumbnailImage ||
        enclosureImage ||
        itunesImageUrl ||
        contentImage,
      baseUrl,
    );

    return {
      title,
      content,
      link,
      date: pubRaw,
      image,
    };
  });
}

function parseAtomEntries(
  doc: Document,
  baseUrl: string,
): Array<{
  title: string;
  content: string;
  link: string;
  date: string;
  image: string;
}> {
  return Array.from(
    doc.querySelectorAll("entry"),
  ).map((element) => {
    const title = cleanText(
      element.querySelector("title")
        ?.textContent ||
        "Sem título",
    );

    const content =
      element.querySelector("summary")
        ?.textContent ||
      element.querySelector("content")
        ?.textContent ||
      "";

    const linkElement =
      element.querySelector(
        'link[rel="alternate"]',
      ) ||
      element.querySelector("link");

    const link =
      linkElement
        ?.getAttribute("href")
        ?.trim() ||
      "";

    const pubRaw =
      element.querySelector("published")
        ?.textContent ||
      element.querySelector("updated")
        ?.textContent ||
      "";

    const thumbnail =
      (
        element.getElementsByTagName(
          "media:thumbnail",
        )[0] as
          | Element
          | undefined
      )?.getAttribute("url") || "";

    const mediaContent =
      (
        element.getElementsByTagName(
          "media:content",
        )[0] as
          | Element
          | undefined
      )?.getAttribute("url") || "";

    const contentImage =
      extractImageFromHtml(
        content,
      );

    const image = resolveUrl(
      mediaContent ||
        thumbnail ||
        contentImage,
      baseUrl,
    );

    return {
      title,
      content,
      link,
      date: pubRaw,
      image,
    };
  });
}

function parseXmlFeed(
  xml: string,
  sourceName: string,
  baseUrl = "",
): NewsItem[] {
  const doc =
    new DOMParser().parseFromString(
      xml,
      "text/xml",
    );

  if (
    doc.querySelector("parsererror")
  ) {
    return [];
  }

  const hasItems =
    doc.querySelectorAll("item")
      .length > 0;

  const hasEntries =
    doc.querySelectorAll("entry")
      .length > 0;

  const raw = hasItems
    ? parseRssItems(doc, baseUrl)
    : hasEntries
      ? parseAtomEntries(
          doc,
          baseUrl,
        )
      : [];

  return raw.map(
    (entry, index) => {
      const parsedDate =
        new Date(entry.date);

      const date = !Number.isNaN(
        parsedDate.getTime(),
      )
        ? parsedDate.toISOString()
        : new Date().toISOString();

      const excerpt =
        cleanText(entry.content);

      return {
        id: index + 1,
        title: entry.title,
        excerpt:
          excerpt.slice(0, 180),
        category: sourceName,
        date,
        image:
          entry.image || "",
        readTime:
          estimateReadTime(excerpt),
        source: sourceName,
        link: resolveUrl(
          entry.link,
          baseUrl,
        ),
      };
    },
  );
}

async function fetchFeedViaProxies(
  feedUrl: string,
): Promise<string> {
  const tasks = PROXIES.map(
    (proxy) => () =>
      fetchWithTimeout(
        proxy +
          encodeURIComponent(
            feedUrl,
          ),
        PROXY_FEED_TIMEOUT_MS,
      ),
  );

  return firstSuccessful(
    tasks,
    (value) =>
      value.trim().length > 0,
  );
}

async function fetchSource(
  source: RssSource,
): Promise<NewsItem[]> {
  const feedUrl =
    source.url.trim();

  const fastTasks: Array<
    () => Promise<NewsItem[]>
  > = [
    async () => {
      const xml =
        await fetchWithTimeout(
          feedUrl,
          DIRECT_FEED_TIMEOUT_MS,
        );

      return parseXmlFeed(
        xml,
        source.name,
        feedUrl,
      );
    },
    async () =>
      fetchViaRss2json(
        feedUrl,
        source.name,
      ),
  ];

  try {
    return await firstSuccessful(
      fastTasks,
      (items) =>
        items.length > 0,
    );
  } catch {
    const xml =
      await fetchFeedViaProxies(
        feedUrl,
      );

    const items =
      parseXmlFeed(
        xml,
        source.name,
        feedUrl,
      );

    if (items.length === 0) {
      throw new Error(
        "A fonte não retornou notícias.",
      );
    }

    return items;
  }
}

export async function fetchAndParseFeeds(
  sources: RssSource[],
  onProgress?: ProgressCallback,
): Promise<NewsItem[]> {
  const enabled = sources.filter(
    (source) =>
      source.enabled &&
      source.url.trim(),
  );

  if (enabled.length === 0) {
    return [];
  }

  const collected: NewsItem[] = [];

  await Promise.allSettled(
    enabled.map(async (source) => {
      const items =
        await fetchSource(source);

      if (items.length === 0) {
        return;
      }

      collected.push(...items);

      onProgress?.(
        finalizeNews(collected),
      );
    }),
  );

  return finalizeNews(
    collected,
  );
}

function readOgCache(): Record<
  string,
  OgCacheEntry
> {
  try {
    const raw =
      localStorage.getItem(
        OG_CACHE_KEY,
      );

    if (!raw) {
      return {};
    }

    return JSON.parse(
      raw,
    ) as Record<
      string,
      OgCacheEntry
    >;
  } catch {
    return {};
  }
}

function writeOgCache(
  cache: Record<
    string,
    OgCacheEntry
  >,
): void {
  try {
    const entries =
      Object.entries(cache)
        .sort(
          (a, b) =>
            b[1].ts - a[1].ts,
        )
        .slice(0, 120);

    localStorage.setItem(
      OG_CACHE_KEY,
      JSON.stringify(
        Object.fromEntries(
          entries,
        ),
      ),
    );
  } catch {
    // ignora quota/storage bloqueado
  }
}

async function fetchOgImage(
  articleUrl: string,
): Promise<string> {
  if (!articleUrl) {
    return "";
  }

  const cache = readOgCache();
  const cached =
    cache[articleUrl];

  if (cached) {
    const ttl = cached.image
      ? OG_CACHE_SUCCESS_TTL
      : OG_CACHE_EMPTY_TTL;

    if (
      Date.now() - cached.ts <
      ttl
    ) {
      return cached.image;
    }
  }

  const tasks = PROXIES.map(
    (proxy) => async () => {
      const html =
        await fetchWithTimeout(
          proxy +
            encodeURIComponent(
              articleUrl,
            ),
          OG_IMAGE_TIMEOUT_MS,
        );

      const image =
        extractImageFromHtml(
          html,
        );

      const resolved =
        resolveUrl(
          image,
          articleUrl,
        );

      if (!resolved) {
        throw new Error(
          "Página sem imagem.",
        );
      }

      return resolved;
    },
  );

  try {
    const image =
      await firstSuccessful(
        tasks,
        (value) =>
          Boolean(value),
      );

    cache[articleUrl] = {
      image,
      ts: Date.now(),
    };

    writeOgCache(cache);

    return image;
  } catch {
    cache[articleUrl] = {
      image: "",
      ts: Date.now(),
    };

    writeOgCache(cache);

    return "";
  }
}

export async function enrichNewsImages(
  news: NewsItem[],
  onUpdate?: ImageUpdateCallback,
): Promise<NewsItem[]> {
  const enriched = news.map(
    (item) => ({ ...item }),
  );

  const pending = enriched
    .filter(
      (item) =>
        !item.image &&
        Boolean(item.link),
    )
    .slice(
      0,
      MAX_OG_LOOKUPS_PER_LOAD,
    );

  if (pending.length === 0) {
    return enriched;
  }

  let cursor = 0;

  const worker = async () => {
    while (cursor < pending.length) {
      const index = cursor;
      cursor += 1;

      const target =
        pending[index];

      if (!target?.link) {
        continue;
      }

      const image =
        await fetchOgImage(
          target.link,
        );

      if (!image) {
        continue;
      }

      const targetIndex =
        enriched.findIndex(
          (item) =>
            item.id === target.id,
        );

      if (targetIndex < 0) {
        continue;
      }

      const updated = {
        ...enriched[targetIndex],
        image,
      };

      enriched[targetIndex] =
        updated;

      onUpdate?.(
        updated,
        enriched.map(
          (item) => ({ ...item }),
        ),
      );
    }
  };

  const workers = Array.from(
    {
      length: Math.min(
        OG_LOOKUP_CONCURRENCY,
        pending.length,
      ),
    },
    () => worker(),
  );

  await Promise.allSettled(
    workers,
  );

  return enriched;
}

function hashSources(
  sources: RssSource[],
): string {
  return sources
    .filter(
      (source) =>
        source.enabled,
    )
    .map(
      (source) =>
        `${source.name}:${source.url}`,
    )
    .join("|");
}

export function getCachedNews(
  sources: RssSource[],
  allowStale = false,
): NewsItem[] | null {
  try {
    const raw =
      localStorage.getItem(
        CACHE_KEY,
      );

    if (!raw) {
      return null;
    }

    const cache =
      JSON.parse(
        raw,
      ) as CacheEntry;

    if (
      hashSources(sources) !==
      cache.sourcesHash
    ) {
      return null;
    }

    if (
      !Array.isArray(cache.news)
    ) {
      return null;
    }

    if (!allowStale) {
      const ageMinutes =
        (Date.now() -
          cache.timestamp) /
        60000;

      if (
        ageMinutes >
        CACHE_TTL_MINUTES
      ) {
        return null;
      }
    }

    return cache.news;
  } catch {
    return null;
  }
}

export function setCachedNews(
  sources: RssSource[],
  news: NewsItem[],
): void {
  try {
    const entry: CacheEntry = {
      news,
      timestamp: Date.now(),
      sourcesHash:
        hashSources(sources),
    };

    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(entry),
    );
  } catch {
    // ignora quota/storage bloqueado
  }
}

export function clearCache(): void {
  try {
    localStorage.removeItem(
      CACHE_KEY,
    );
  } catch {
    // ignora
  }
}
